import { ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { Camera } from '../state';
import { CameraScannerContext } from './CameraScannerContext';

const REAR_KEYWORDS = ['back', 'rear', 'environment', 'world'];

const detectIsRear = (label: string) => {
    const lower = label.toLowerCase();

    return REAR_KEYWORDS.some((keyword) => lower.includes(keyword));
};

export const CameraScannerProvider = ({ children }: { children: ReactNode }) => {
    const [cameras, setCameras] = useState<Camera[]>([]);
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadingRef = useRef(false);

    const refreshDevices = useCallback(async () => {
        if (loadingRef.current) return;

        loadingRef.current = true;
        setLoading(true);
        setError(null);

        try {
            if (!navigator.mediaDevices?.enumerateDevices) {
                throw new Error('Camera API is not available.');
            }

            const devices = await navigator.mediaDevices.enumerateDevices();

            const videoDevices = devices
                .filter((device) => device.kind === 'videoinput')
                .map<Camera>((device, index) => ({
                    deviceId: device.deviceId,
                    label: device.label || `Camera ${index + 1}`,
                    isRearCamera: detectIsRear(device.label || ''),
                }));

            setCameras(videoDevices);
            setLoaded(true);
        } catch (error) {
            console.error('[CameraScannerProvider] failed to enumerate cameras', error);
            setError(error instanceof Error ? error.message : 'Failed to access camera devices');
        } finally {
            loadingRef.current = false;
            setLoading(false);
        }
    }, []);

    const defaultCameraId = useMemo(() => {
        return cameras.find((camera) => camera.isRearCamera)?.deviceId ?? cameras[0]?.deviceId ?? null;
    }, [cameras]);

    const value = useMemo(
        () => ({
            cameras,
            defaultCameraId,
            loading,
            loaded,
            error,
            refreshDevices,
        }),
        [cameras, defaultCameraId, loading, loaded, error, refreshDevices],
    );

    return <CameraScannerContext.Provider value={value}>{children}</CameraScannerContext.Provider>;
};
